import React from 'react';
import NotificationIcon, { NOTIFICATION_TONE_CLASSES } from './NotificationIcon';
import { normalizeNotification } from '../../utils/notificationUtils';

const NotificationItem = ({
  notification,
  compact = false,
  selectable = false,
  selected = false,
  onSelect,
  onOpen,
  onDismiss,
  showUnreadDot = false,
  showCategory = true,
  showStatus = true,
  timeLabel = '',
  children,
  className = '',
}) => {
  const item = normalizeNotification(notification);
  const tone = NOTIFICATION_TONE_CLASSES[item.severity] || NOTIFICATION_TONE_CLASSES.default;
  const rowPadding = compact ? 'px-4 py-3' : 'px-4 py-3';
  const isClickable = Boolean(onOpen);
  const handleOpen = () => {
    if (isClickable) onOpen(item);
  };

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={handleOpen}
      onKeyDown={(event) => {
        if (!isClickable) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleOpen();
        }
      }}
      className={`border-b border-gray-100 last:border-b-0 transition-colors hover:bg-gray-50 dark:border-gray-700/50 dark:hover:bg-gray-700/30 ${
        selected ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
      } ${isClickable ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900/40' : ''} ${className}`}
    >
      <div className={`flex items-start gap-3 ${rowPadding}`}>
        {selectable && (
          <input
            type="checkbox"
            checked={selected}
            onClick={(event) => event.stopPropagation()}
            onChange={() => onSelect?.(item.notification_id)}
            className="mt-1 flex-shrink-0 cursor-pointer accent-blue-500"
          />
        )}

        {showUnreadDot && (
          item.isUnread ? (
            <div className={`mt-2 h-2 w-2 flex-shrink-0 rounded-full ${tone.dot}`} />
          ) : (
            <div className="w-2 flex-shrink-0" />
          )
        )}

        <div className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${tone.iconWrap}`}>
          <NotificationIcon icon={item.icon} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            {onOpen ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpen();
                }}
                className="truncate text-left text-sm font-semibold text-gray-900 hover:underline dark:text-white"
              >
                {item.title}
              </button>
            ) : (
              <span className="truncate text-left text-sm font-semibold text-gray-900 dark:text-white">
                {item.title}
              </span>
            )}
            {timeLabel && (
              <span className="ml-2 flex-shrink-0 text-[10px] text-gray-400 dark:text-gray-500">
                {timeLabel}
              </span>
            )}
            {onDismiss && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onDismiss(item);
                }}
                className="text-gray-300 transition-colors hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400"
                aria-label="Dismiss notification"
                title="Dismiss"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          <p className={`${compact ? 'line-clamp-2 text-xs' : 'text-sm'} mt-0.5 break-words text-gray-600 dark:text-gray-400`}>
            {item.message}
          </p>

          {item.type === 'email_sync' && item.sender_email && (
            <p
              className="mt-1 truncate text-xs font-medium text-teal-700 dark:text-teal-300"
              title={item.sender_email}
            >
              Sender: {item.sender_email}
            </p>
          )}
          {item.type === 'email_sync' && (item.job_title || item.response_type) && (
            <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
              {[item.job_title, item.response_type].filter(Boolean).join(' · ')}
            </p>
          )}

          {(showCategory || showStatus) && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {showCategory && (
                <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${tone.badge}`}>
                  {item.categoryLabel}
                </span>
              )}
              {showStatus && item.statusLabel && (
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    item.has_draft
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  {item.statusLabel}
                </span>
              )}
            </div>
          )}

          {children}
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
